import Button from '@base/components/src/Button';
import FormHelperText from '@base/components/src/FormHelperText';
import FormTextField from '@base/components/src/FormTextField';
import WarningIcon from '@base/components/src/Icon/WarningIcon';
import { GiftCardRequest } from '@package/core/src/giftCard/types';
import { DiscountErrorType } from '@package/core/src/shoppingCart/types';
import useGiftCard from '@package/hooks/src/useGiftCard';
import { ascii, compose, FormErrorCode, required } from '@package/utils/src/formValidation';
import clsx from 'clsx';
import { Form, FormikHelpers, Formik, Field } from 'formik';
import { useCallback, useState } from 'react';

import GiftCardList from '../GiftCardList';
import styles from './GiftCardForm.module.css';
import { GiftCardFormProps, BBMESSAGE } from './types';
import dynamic from 'next/dynamic';

const BBDialog = dynamic(() => import('../BullseyeBucks/components/BBDialog'), { ssr: false });

const giftCardInitialValues = {
  giftCardCode: '',
  receiverLastName: '',
};

const GiftCardForm = ({
  id,
  fullWidth = false,
  buttonLabel,
  giftCardError,
  giftCards,
  removeGiftCardError,
  onDelete,
}: GiftCardFormProps) => {
  const { error, applyGiftCard } = useGiftCard();
  const [isBBDialogOpen, setIsBBDialogOpen] = useState<boolean>(false);

  const handleSubmit = useCallback(async (values: GiftCardRequest, formikHelpers: FormikHelpers<GiftCardRequest>) => {
    if (removeGiftCardError) {
      removeGiftCardError(DiscountErrorType.GiftCardError);
    }
    await applyGiftCard({
      giftCardCode: values.giftCardCode.replace(/\s/g, ''),
      receiverLastName: values.receiverLastName
    });
    formikHelpers.setValues(giftCardInitialValues);
    formikHelpers.setTouched({
      giftCardCode: false,
      receiverLastName: false,
    });
  }, []);

  const isBBError = error?.message === BBMESSAGE || giftCardError?.message === BBMESSAGE;
  const errorMessage = error?.message ? error.message : giftCardError?.message;
  const handleBBModelOpen = () => {
    if (isBBError) {
      setIsBBDialogOpen(true);
    }
  };

  return (
    <>
      <Formik<GiftCardRequest> initialValues={giftCardInitialValues} onSubmit={handleSubmit}>
        {({ handleSubmit, setFieldValue }) => (
          <Form
            id={id}
            className={clsx(styles.bonusForm, {
              [styles.bonusFormFullWidth]: fullWidth,
            })}
            onSubmit={handleSubmit}
          >
            <Field
              id={`${id}-input`}
              data-testid={`${id}-input`}
              className={styles.giftCardInput}
              fullWidth
              name="giftCardCode"
              label={'Gift-Card #'}
              autoComplete="off"
              labelClassName={styles.giftCardFormLabel}
              validate={compose(
                required,
                ascii(() => setFieldValue('giftCardCode', ''), FormErrorCode.EnglishAlphabet),
              )}
              component={FormTextField}
            />
            <Field
              id={`${id}-last-name`}
              data-testid={`${id}-last-name`}
              className={styles.giftCardInput}
              fullWidth
              name="receiverLastName"
              label={'Last Name'}
              labelClassName={styles.giftCardFormLabel}
              autoComplete="off"
              validate={compose(
                required,
                ascii(() => setFieldValue('receiverLastName', ''), FormErrorCode.Alphabet),
              )}
              component={FormTextField}
            />
            <Button
              id={`${id}-apply-btn`}
              data-testid={`${id}-apply-btn`}
              fullWidth
              type="submit"
              size={'large'}
              variant={'outlined'}
              label={buttonLabel}
              classes={styles.giftCardApplyButton}
            />
            {(Boolean(error) || Boolean(giftCardError?.message)) && (
              <div className={styles.giftCardZoom}>
                <FormHelperText error>
                  <span
                    id={`${id}-error-message`}
                    className={clsx(styles.giftCardFormErrorMessage, { [styles.bbErrorLink]: isBBError })}
                  >
                    <span className={styles.giftCardWarningIconContainer}>
                      <WarningIcon />
                    </span>
                    {errorMessage && (
                      <span dangerouslySetInnerHTML={{ __html: errorMessage }} onClick={handleBBModelOpen} />
                    )}
                  </span>
                </FormHelperText>
              </div>
            )}
            <GiftCardList
              id="gift-card-list"
              title="Applied Gift Cards"
              data={giftCards}
              onDelete={(serialNumber: string) => onDelete({ serialNumber })}
            />
          </Form>
        )}
      </Formik>
      {isBBError && <BBDialog isOpen={isBBDialogOpen} handleCloseBBDialog={() => setIsBBDialogOpen(false)} />}
    </>
  );
};

export default GiftCardForm;
